import _ from 'lodash';
import type qs from 'qs';
import { useCallback, useMemo } from 'react';
import type { Infer, Struct } from 'superstruct';
import { mask } from 'superstruct';

import { firstValue } from '../nav-action';

import { atPath, Lens, lensFrom, useUrlQueryLens } from './lens';

export function queryStringifyStruct<
	T extends Struct<Record<string, unknown>, unknown>,
>(value: Infer<T> | undefined): string | undefined {
	if (value === undefined) {
		return value;
	}

	return JSON.stringify(value, (_key, value_) => {
		// Remove empty arrays and empty objects.
		if (typeof value_ === 'object' && value_ !== null && _.isEmpty(value_)) {
			return undefined;
		}

		return value_;
	});
}

export function urlQueryStructLens<
	T extends Struct<Record<string, unknown>, unknown>,
>(path: string, struct: T) {
	return lensFrom<qs.ParsedQs>()
		.then(atPath(path))
		.then<Infer<T> | undefined, Infer<T> | undefined>(
			new Lens(
				(value) => {
					try {
						value = firstValue(value);
						if (value === undefined) {
							return undefined;
						}
						return mask(JSON.parse(value), struct);
					} catch (err) {
						console.error(
							'Error occurred when parsing values from URL query:',
							{ err, path, struct, origValue: value }
						);
						return undefined;
					}
				},
				(value) => {
					if (value === undefined) {
						return value;
					}
					try {
						const maskedValue = mask(value, struct);
						// remove params such as empty arrays/objects
						return queryStringifyStruct(maskedValue);
					} catch (err) {
						console.error(
							'Error occurred when validating values to URL query:',
							{ err, path, struct, origValue: value }
						);
						return undefined;
					}
				}
			)
		);
}

/**
 * Wrapper around useUrlQuery that accepts a superstruct struct
 * for validation and typing.
 *
 * NOTE: Struct arrays must use `parsableArray` for defining array
 * types due to how this hook persists arrays to the URL query.
 */
export function useUrlQueryStruct<
	T extends Struct<Record<string, unknown>, unknown>,
>(path: string, struct: T) {
	const [value, setValue] = useUrlQueryLens(
		useMemo(() => urlQueryStructLens(path, struct), [path, struct])
	);

	return {
		value,
		setValue,
		clearValue: useCallback(() => setValue(undefined), [setValue]),
	};
}
